/**
 * Column definitions for the Recurring transactions table.
 *
 * Single source of truth for both the head (RecurringTableHead) and
 * each row (RecurringRow). The visibility hook, the Customize popover,
 * and the grid-template-columns calculations all read from this list.
 * Add a new column by appending to COLUMNS below.
 */

export type ColumnKey =
  | "name"
  | "type"
  | "txnType"
  | "interval"
  | "previous"
  | "next"
  | "party"
  | "amount"
  | "action";

export interface ColumnDef {
  key: ColumnKey;
  label: string;
  /** CSS grid-template-columns value for this column's width. */
  width: string;
  /** User cannot hide this column. Shown locked in the popover with a Required chip. */
  required?: boolean;
  /** Default visibility if the user has no stored preference yet. */
  defaultVisible: boolean;
  /** Hide from the Customize popover entirely (used for the Action column). */
  hideFromCustomize?: boolean;
  /** Right-align the cell contents (for Amount). */
  rightAlign?: boolean;
  /** Column header shows a sort arrow and is clickable. */
  sortable?: boolean;
}

/** The 9 columns in the Recurring table, in display order. */
export const COLUMNS: readonly ColumnDef[] = [
  {
    key: "name",
    label: "Template name",
    width: "minmax(220px, 2.2fr)",
    required: true,
    defaultVisible: true,
    sortable: true,
  },
  {
    key: "type",
    label: "Type",
    width: "110px",
    defaultVisible: true,
  },
  {
    key: "txnType",
    label: "Txn type",
    width: "110px",
    defaultVisible: true,
  },
  {
    key: "interval",
    label: "Interval",
    width: "minmax(150px, 1.4fr)",
    defaultVisible: true,
  },
  {
    key: "previous",
    label: "Previous date",
    width: "110px",
    defaultVisible: true,
  },
  {
    key: "next",
    label: "Next date",
    width: "130px",
    required: true,
    defaultVisible: true,
    sortable: true,
  },
  {
    key: "party",
    label: "Customer/Supplier",
    width: "minmax(140px, 1.3fr)",
    defaultVisible: true,
  },
  {
    key: "amount",
    label: "Amount",
    width: "110px",
    defaultVisible: true,
    rightAlign: true,
  },
  {
    key: "action",
    label: "Action",
    width: "60px",
    required: true,
    defaultVisible: true,
    hideFromCustomize: true,
  },
] as const;

/** Set of column keys visible by default (first load, no stored preference). */
export const DEFAULT_VISIBLE_COLUMNS: ReadonlySet<ColumnKey> = new Set(
  COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key),
);

/** Set of column keys the user cannot hide. */
export const REQUIRED_COLUMNS: ReadonlySet<ColumnKey> = new Set(
  COLUMNS.filter((c) => c.required).map((c) => c.key),
);

/** Columns the user can toggle in the Customize popover (excludes Action). */
export const CUSTOMIZABLE_COLUMNS: readonly ColumnDef[] = COLUMNS.filter(
  (c) => !c.hideFromCustomize,
);

/**
 * Compute the CSS grid-template-columns string for a given visible set.
 * Hidden columns are REMOVED from the grid entirely, so remaining
 * columns re-flow and breathe (not just shrunk to zero width).
 */
export function computeGridTemplate(visible: ReadonlySet<ColumnKey>): string {
  return COLUMNS.filter((c) => visible.has(c.key))
    .map((c) => c.width)
    .join(" ");
}

/** The localStorage key for this table's column visibility preferences. */
export const RECURRING_COLUMNS_STORAGE_KEY = "novala.recurring.visibleColumns";